import React, { useState, useEffect } from 'react';
import { User, Reservation, ReservationStatus, Slot, UserRole, Gym, Zone } from '../types';
import { MOCK_GYMS } from '../constants';
import { createClient } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import { Html5Qrcode } from 'html5-qrcode';
import {
  Users,
  QrCode,
  Clock,
  UserPlus,
  Loader2,
  Plus,
  Trash2,
  CheckCircle2,
  X,
  Key,
  Wand2,
  Check,
  AlertCircle,
  CalendarDays,
  MapPin,
  Building2,
  Globe,
  MessageSquare
} from 'lucide-react';
import Chat from './Chat';
import LegalFooter from './LegalFooter';
import ChangePasswordModal from './ChangePasswordModal';

interface GymPortalProps {
  user: User;
  reservations: Reservation[];
  setReservations: React.Dispatch<React.SetStateAction<Reservation[]>>;
  gyms: Gym[];
  zones: Zone[];
}

const DAYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

// Cliente temporal para crear usuarios sin cerrar la sesión del staff
const supabaseUrl = 'https://hshqshiznfwasvgtcjaj.supabase.co';
const supabaseKey = 'sb_publishable_xxWVLD9FFM8DgybrkMlnGg_Gjn3VH6U';
const tempSupabase = createClient(supabaseUrl, supabaseKey, {
  auth: { persistSession: false }
});

const GymPortal: React.FC<GymPortalProps> = ({ user, reservations, setReservations, gyms, zones }) => {
  const [activeTab, setActiveTab] = useState<'reservations' | 'slots' | 'clients' | 'allied-gyms' | 'chat'>('reservations');
  const [selectedChatClient, setSelectedChatClient] = useState<{ id: string, name: string } | null>(null);
  const [chatConversations, setChatConversations] = useState<{ id: string, name: string }[]>([]);
  const [unreadClientIds, setUnreadClientIds] = useState<Set<string>>(new Set());
  const [reservationClientNames, setReservationClientNames] = useState<Record<string, string>>({});
  const [authEmails, setAuthEmails] = useState<any[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(false);

  // States para formularios
  const [isAddingSlot, setIsAddingSlot] = useState(false);
  const [newSlot, setNewSlot] = useState({ day: 1, start: "08:00", end: "09:30", cap: 5 });
  const [editingSlotId, setEditingSlotId] = useState<string | null>(null);
  const [editingCapacity, setEditingCapacity] = useState<number>(0);

  const [isAddingClient, setIsAddingClient] = useState(false);
  const [isDeletingClient, setIsDeletingClient] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newPin, setNewPin] = useState('');

  const [showPins, setShowPins] = useState<Record<string, boolean>>({});
  const [clientToDelete, setClientToDelete] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState(false);

  const [showScanner, setShowScanner] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [isProcessingCode, setIsProcessingCode] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const hasGymId = !!user.gymId;
  const gym = gyms.find(g => g.id === user.gymId);

  // 🔥 SOLO reservas activas y futuras (según fecha + hora del slot)
  const gymReservations = reservations
    .filter(r => r.gymId === user.gymId)
    .filter(r => r.status === ReservationStatus.CONFIRMED)
    .filter(r => {
      const slot = slots.find(s => s.id === r.slotId);
      if (!slot) return false;

      const now = new Date();

      const [year, month, day] = r.date.split('-').map(Number);
      const [hour, minute] = slot.endTime.split(':').map(Number);

      const reservationDateTime = new Date(year, month - 1, day, hour, minute);

      return reservationDateTime >= now;
    })
    .sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);

      const slotA = slots.find(s => s.id === a.slotId);
      const slotB = slots.find(s => s.id === b.slotId);

      if (slotA && slotB) {
        return slotA.startTime.localeCompare(slotB.startTime);
      }
      return 0;
    });

  const getTodayISO = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const todayISO = getTodayISO();
  const todayCount = gymReservations.filter(r => r.date === todayISO).length;

  // ✅ Total visitas (check-ins exitosos)
  const totalVisits = reservations.filter(r => r.status === ReservationStatus.CHECKED_IN).length;

  // ✅ Gimnasios aliados (fuera de mi zona)
  const alliedGyms = gyms.filter(g => g.id !== user.gymId && g.zoneId !== gym?.zoneId);

  useEffect(() => {
    if (hasGymId) {
      fetchSlots();
      fetchData();
      fetchUnreadMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user.gymId]);

  useEffect(() => {
    if (!hasGymId) return;

    const channel = supabase
      .channel('gym_unread_messages')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `gym_id=eq.${user.gymId}`,
        },
        () => {
          fetchUnreadMessages();
          if (activeTab === 'chat') fetchChatConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user.gymId, activeTab]);

  useEffect(() => {
    if (activeTab === 'reservations' && gymReservations.length > 0) {
      fetchReservationClientNames();
    }
    if (activeTab === 'reservations' && gymReservations.length === 0) {
      setReservationClientNames({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, gymReservations.length]);

  const fetchUnreadMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('client_id')
        .eq('gym_id', user.gymId)
        .neq('is_read', true)
        .eq('sender_role', 'CLIENT');

      if (error) throw error;

      const clientIds = new Set((data || []).map(m => m.client_id));
      setUnreadClientIds(clientIds);
    } catch (err) {
      console.error('Error fetching unread messages:', err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'clients') await fetchAuthorizedEmails();
      if (activeTab === 'chat') await fetchChatConversations();
      if (activeTab === 'slots') await fetchSlots();
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchChatConversations = async () => {
    try {
      const { data: msgData, error: msgError } = await supabase
        .from('messages')
        .select('client_id')
        .eq('gym_id', user.gymId);

      if (msgError) throw msgError;

      const uniqueClientIds = Array.from(new Set((msgData || []).map(m => m.client_id)));

      if (uniqueClientIds.length === 0) {
        setChatConversations([]);
        return;
      }

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', uniqueClientIds);

      if (profileError) throw profileError;

      setChatConversations((profileData || []).map(p => ({
        id: p.id,
        name: p.full_name
      })));
    } catch (err) {
      console.error("Error fetching chat conversations:", err);
    }
  };

  const fetchReservationClientNames = async () => {
    try {
      const clientIds = Array.from(
        new Set(
          gymReservations
            .map(r => r.clientId)
            .filter((id): id is string => !!id)
        )
      );

      if (clientIds.length === 0) {
        setReservationClientNames({});
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', clientIds);

      if (error) throw error;

      const namesMap: Record<string, string> = {};
      (data || []).forEach(profile => {
        if (profile.id) {
          namesMap[profile.id] = profile.full_name;
        }
      });

      setReservationClientNames(namesMap);
    } catch (err) {
      console.error('Error fetching reservation client names:', err);
    }
  };

  const openChatFromReservation = (clientId?: string | null) => {
    if (!clientId) return;

    setSelectedChatClient({
      id: clientId,
      name: reservationClientNames[clientId] ?? 'Socio'
    });

    setActiveTab('chat');
  };

  const fetchSlots = async () => {
    const { data } = await supabase
      .from('slots')
      .select('*')
      .eq('gym_id', user.gymId)
      .order('day_of_week', { ascending: true });

    if (data) {
      setSlots(data.map(d => ({
        id: d.id,
        gymId: d.gym_id,
        dayOfWeek: d.day_of_week,
        startTime: d.start_time.slice(0, 5),
        endTime: d.end_time.slice(0, 5),
        capacity: d.capacity,
        isActive: d.is_active
      })));
    }
  };

  const fetchAuthorizedEmails = async () => {
    const { data, error } = await supabase
      .from('authorized_emails')
      .select('*')
      .eq('gym_id', user.gymId)
      .order('created_at', { ascending: false });

    if (!error && data) setAuthEmails(data);
  };

  const handleAddSlot = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAddingSlot(true);
    try {
      const { error } = await supabase.from('slots').insert([{
        gym_id: user.gymId,
        day_of_week: newSlot.day,
        start_time: newSlot.start,
        end_time: newSlot.end,
        capacity: newSlot.cap,
        is_active: true
      }]);
      if (error) throw error;
      await fetchSlots();
      setNewSlot({ day: 1, start: "08:00", end: "09:30", cap: 5 });
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsAddingSlot(false);
    }
  };

  const generateDefaultSlots = async () => {
    if (!user.gymId) return;
    setLoading(true);
    try {
      const defaultSlots = [];
      // day_of_week: 1 (Monday) to 5 (Friday)
      for (let day = 1; day <= 5; day++) {
        defaultSlots.push({
          gym_id: user.gymId,
          day_of_week: day,
          start_time: '11:00:00',
          end_time: '16:00:00',
          capacity: 10,
          is_active: true
        });
      }

      const { error } = await supabase.from('slots').insert(defaultSlots);
      if (error) throw error;
      
      // Refresh slots
      const { data } = await supabase
        .from('slots')
        .select('*')
        .eq('gym_id', user.gymId)
        .order('day_of_week', { ascending: true })
        .order('start_time', { ascending: true });
      
      if (data) setSlots(data as Slot[]);
      alert("Horarios generados exitosamente (Lunes a Viernes, 11:00 - 16:00)");
    } catch (err: any) {
      alert("Error al generar horarios: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteSlot = async (id: string) => {
    const activeReservations = reservations.filter(r =>
      r.slotId === id &&
      (r.status === ReservationStatus.CONFIRMED || r.status === ReservationStatus.CHECKED_IN)
    );

    if (activeReservations.length > 0) {
      alert("No puedes eliminar este horario porque tiene reservas activas o socios que ya asistieron. Solo puedes eliminar horarios sin actividad o con todas las reservas canceladas.");
      return;
    }

    if (!confirm("¿Estás seguro de que deseas eliminar este horario? Esto desvinculará las reservas canceladas pasadas y eliminará el bloque permanentemente.")) return;

    try {
      const { error: unlinkError } = await supabase
        .from('reservations')
        .update({ slot_id: null })
        .eq('slot_id', id);

      if (unlinkError) throw unlinkError;

      const { error: deleteError } = await supabase
        .from('slots')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      fetchSlots();
    } catch (err: any) {
      alert("Error al procesar la eliminación: " + err.message);
    }
  };

  const updateSlotCapacity = async (id: string, newCap: number) => {
    const activeReservationsCount = reservations.filter(r =>
      r.slotId === id &&
      (r.status === ReservationStatus.CONFIRMED || r.status === ReservationStatus.CHECKED_IN)
    ).length;

    if (newCap < activeReservationsCount) {
      alert(`No puedes reducir la capacidad a ${newCap} porque ya tienes ${activeReservationsCount} reservas activas para este horario.`);
      return;
    }

    const { error } = await supabase
      .from('slots')
      .update({ capacity: newCap })
      .eq('id', id);

    if (error) {
      alert("Error al actualizar: " + error.message);
    } else {
      setEditingSlotId(null);
      fetchSlots();
    }
  };

  const handleDeleteClient = async (email: string) => {
    setIsDeletingClient(true);
    try {
      const { error } = await supabase
        .from('authorized_emails')
        .delete()
        .eq('email', email)
        .eq('gym_id', user.gymId);

      if (error) throw error;

      setClientToDelete(null);
      await fetchAuthorizedEmails();
    } catch (err: any) {
      alert("Error al eliminar socio: " + err.message);
    } finally {
      setIsDeletingClient(false);
    }
  };

  const handleAddClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPin || newPin.length < 6) {
      alert("El PIN debe ser de al menos 6 caracteres.");
      return;
    }

    setIsAddingClient(true);
    try {
      const cleanEmail = newEmail.toLowerCase().trim();

      const { data: authData, error: authError } = await tempSupabase.auth.signUp({
        email: cleanEmail,
        password: newPin,
        options: {
          data: {
            full_name: newName,
            role: UserRole.CLIENT
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error("No se pudo crear el usuario en Auth.");

      const userId = authData.user.id;

      const { error: authEmailError } = await supabase.from('authorized_emails').insert([{
        email: cleanEmail,
        full_name: newName,
        gym_id: user.gymId,
        zone_id: gym?.zoneId,
        pin_code: newPin,
        is_active: true
      }]);

      if (authEmailError) throw authEmailError;

      const { error: profileError } = await supabase.from('profiles').insert([{
        id: userId,
        full_name: newName,
        role: UserRole.CLIENT,
        home_gym_id: user.gymId,
        benefit_active: true
      }]);

      if (profileError && !profileError.message.includes('duplicate key')) {
        console.warn("Error creando perfil, pero el usuario se creó:", profileError.message);
      }

      setNewEmail('');
      setNewName('');
      setNewPin('');
      setSuccessMsg(true);
      setTimeout(() => setSuccessMsg(false), 3000);
      await fetchAuthorizedEmails();
    } catch (err: any) {
      alert("Error en el alta: " + err.message);
    } finally {
      setIsAddingClient(false);
    }
  };

  const confirmCheckIn = async (resId: string) => {
    const { error } = await supabase
      .from('reservations')
      .update({ status: ReservationStatus.CHECKED_IN })
      .eq('id', resId);

    if (!error) {
      setReservations(prev => prev.map(r => r.id === resId ? { ...r, status: ReservationStatus.CHECKED_IN } : r));
    }
  };

  const handleCheckInByCode = async (code: string) => {
    if (!code.trim()) return;
    setIsProcessingCode(true);
    try {
      const { data, error } = await supabase
        .from('reservations')
        .select('*')
        .eq('qr_code', code.trim())
        .eq('gym_id', user.gymId)
        .eq('status', ReservationStatus.CONFIRMED)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        alert("Ticket inválido, ya usado o de otra sede.");
        return;
      }

      const today = new Date().toISOString().split('T')[0];
      if (data.reservation_date !== today) {
        alert(`Este ticket es para la fecha ${data.reservation_date.split('-').reverse().join('/')}, no para hoy.`);
        return;
      }

      await confirmCheckIn(data.id);
      alert("¡Check-in exitoso para el ticket " + code + "!");
      setManualCode('');
      setShowScanner(false);
    } catch (err: any) {
      alert("Error: " + err.message);
    } finally {
      setIsProcessingCode(false);
    }
  };

  useEffect(() => {
    let html5QrCode: Html5Qrcode | null = null;

    if (showScanner) {
      html5QrCode = new Html5Qrcode("reader");
      const config = { fps: 10, qrbox: { width: 250, height: 250 } };

      html5QrCode.start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          handleCheckInByCode(decodedText);
          if (html5QrCode) {
            html5QrCode.stop().catch(err => console.error("Error stopping scanner", err));
          }
        },
        () => {}
      ).catch(err => {
        console.error("Error starting scanner", err);
        alert("No se pudo acceder a la cámara. Asegúrate de dar permisos.");
        setShowScanner(false);
      });
    }

    return () => {
      if (html5QrCode && html5QrCode.isScanning) {
        html5QrCode.stop().catch(err => console.error("Error stopping scanner on cleanup", err));
      }
    };
  }, [showScanner]);

  return (
    <div className="space-y-6">
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}
      {!hasGymId && (
        <div className="bg-rose-50 border border-rose-200 p-6 rounded-3xl flex items-start gap-4">
          <AlertCircle className="text-rose-600 shrink-0" size={24} />
          <div>
            <h4 className="font-black text-rose-800 uppercase text-[10px] tracking-widest mb-1">Perfil Incompleto</h4>
            <p className="text-xs text-rose-600 font-medium leading-relaxed">
              Tu cuenta no tiene un ID de sede asignado. Edita tu perfil en la tabla <b>profiles</b> de Supabase.
            </p>
          </div>
        </div>
      )}

      <header className="glass-card p-6 sm:p-10 relative overflow-hidden border-brand/20">
        <div className="absolute top-0 right-0 w-80 h-80 bg-brand/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:justify-between md:items-center gap-8">
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-brand rounded-full flex items-center justify-center text-black shadow-[0_0_30px_rgba(255,255,255,0.2)]">
              <Building2 size={32} className="sm:w-10 sm:h-10" />
            </div>
            <div>
              <div className="flex items-center gap-2 sm:gap-3 mb-2">
                <span className="bg-brand/10 text-brand text-[8px] sm:text-[9px] font-bold px-2 sm:px-2.5 py-1 rounded border border-brand/20 uppercase tracking-widest">Staff</span>
                <span className="text-white/20 text-[8px] sm:text-[9px] font-bold uppercase tracking-widest">
                  {zones.find(z => z.id === gym?.zoneId)?.name || 'Sin Zona'}
                </span>
                <span className="text-white/10 text-[8px] sm:text-[9px] font-bold uppercase tracking-widest">ID: {user.gymId || '---'}</span>
              </div>
              <h1 className="text-3xl sm:text-5xl font-display uppercase tracking-tighter text-white leading-none">{gym?.name || 'Gimnasio'}</h1>
              <div className="flex items-center gap-2 mt-2">
                <MapPin size={12} className="text-brand sm:w-4 sm:h-4" />
                <p className="text-[9px] sm:text-[11px] text-white/60 font-bold uppercase tracking-widest">{gym?.address}</p>
              </div>
              <div className="flex items-center gap-4 mt-3">
                <p className="text-white/40 text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.3em]">Elite Performance Management</p>
                <button 
                  onClick={() => setShowChangePassword(true)}
                  className="text-white/20 hover:text-brand text-[8px] sm:text-[9px] font-bold uppercase tracking-widest transition-colors flex items-center gap-1.5"
                >
                  <Key size={12} />
                  Cambiar Clave
                </button>
              </div>
              <p className="text-brand text-[10px] sm:text-[11px] font-display uppercase tracking-[0.2em] mt-1 italic opacity-80">be a bro</p>
            </div>
          </div>
          <div className="flex gap-3 sm:gap-4">
            {unreadClientIds.size > 0 && (
              <button
                onClick={() => setActiveTab('chat')}
                className="glass-card px-4 sm:px-6 py-3 sm:py-4 flex flex-col items-center border-emerald-500/20 bg-emerald-500/5 hover:bg-emerald-500/10 transition-all shadow-[0_0_20px_rgba(16,185,129,0.1)]"
              >
                <p className="text-[8px] sm:text-[9px] uppercase font-bold text-emerald-500/60 tracking-[0.2em] mb-1">Mensajes</p>
                <p className="font-display text-2xl sm:text-3xl text-emerald-500">{unreadClientIds.size}</p>
              </button>
            )}
            <div className="glass-card px-6 sm:px-8 py-3 sm:py-4 flex flex-col items-center border-white/5">
              <p className="text-[8px] sm:text-[9px] uppercase font-bold text-white/30 tracking-[0.2em] mb-1">Visitas Totales</p>
              <p className="font-display text-2xl sm:text-3xl text-brand">{totalVisits}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="flex gap-2 bg-white/5 p-1 rounded-xl border border-white/5 w-full overflow-x-auto no-scrollbar">
        {[
          { id: 'reservations', label: 'Check-in', icon: CheckCircle2 },
          { id: 'slots', label: 'Horarios', icon: Clock },
          { id: 'clients', label: 'Mis Socios', icon: Users },
          { id: 'chat', label: 'Mensajes', icon: MessageSquare },
          { id: 'allied-gyms', label: 'Red Aliada', icon: Globe }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex-1 px-4 sm:px-8 py-3 sm:py-4 rounded-lg text-[9px] sm:text-[10px] font-display uppercase tracking-widest flex items-center justify-center gap-2 sm:gap-3 transition-all whitespace-nowrap relative ${activeTab === tab.id ? 'bg-brand text-black' : 'text-white/40 hover:bg-white/5'}`}
          >
            <tab.icon size={14} className="sm:w-4 sm:h-4" /> {tab.label}
            {tab.id === 'chat' && unreadClientIds.size > 0 && (
              <span className="absolute top-2 right-2 w-3 h-3 bg-emerald-500 rounded-full border-2 border-dark-bg shadow-[0_0_10px_rgba(16,185,129,0.8)]"></span>
            )}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {activeTab === 'reservations' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
              <div className="glass-card overflow-hidden border-white/5">
                <div className="px-6 sm:px-10 py-4 sm:py-6 border-b border-white/5 bg-white/2 font-display text-[9px] sm:text-[10px] uppercase tracking-[0.2em] flex justify-between items-center">
                  <span className="text-white/40">Reservas Activas</span>
                  <span className="bg-brand text-black px-3 py-1 rounded-none font-display">{todayCount} Hoy</span>
                </div>
                <div className="divide-y divide-white/5">
                  {gymReservations.length === 0 ? (
                    <div className="p-12 sm:p-20 text-center">
                      <CalendarDays size={48} className="text-white/5 mx-auto mb-6" />
                      <p className="text-white/20 font-display uppercase tracking-widest">Sin reservas activas</p>
                    </div>
                  ) : (
                    gymReservations.map(res => (
                      <div key={res.id} className="p-6 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 sm:gap-8 hover:bg-white/5 transition-all group">
                        <div className="flex items-center gap-4 sm:gap-8">
                          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/5 rounded-xl flex items-center justify-center text-white/20 group-hover:text-brand transition-colors border border-white/5">
                            <QrCode size={24} className="sm:w-8 sm:h-8" />
                          </div>
                          <div>
                            <div className="flex items-center gap-3 mb-1 sm:mb-2 flex-wrap">
                              <p className="font-display text-xl sm:text-2xl uppercase tracking-tight text-white">
                                #{res.id.slice(-6).toUpperCase()}
                              </p>

                              <span className="text-[8px] sm:text-[9px] font-bold px-2 py-0.5 rounded-none uppercase tracking-widest bg-brand/10 text-brand border border-brand/20">
                                PENDIENTE
                              </span>

                              {res.clientId && (
                                <button
                                  onClick={() => openChatFromReservation(res.clientId)}
                                  className="text-[8px] sm:text-[9px] font-bold px-2 py-0.5 rounded-none uppercase tracking-widest bg-white/5 text-white/70 border border-white/10 hover:bg-white hover:text-black transition-all"
                                >
                                  Enviar mensaje
                                </button>
                              )}
                            </div>
                            <div className="flex items-center gap-2 sm:gap-3">
                              <span className="text-[9px] sm:text-[10px] font-mono text-white/40 uppercase tracking-widest">
                                {res.date.split('-').reverse().join('/')}
                              </span>
                              <div className="w-1 h-1 bg-white/10 rounded-full"></div>
                              <p className="text-[9px] sm:text-[10px] text-white/20 font-bold uppercase tracking-widest">Socio Red Elite</p>
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => confirmCheckIn(res.id)}
                          className="brutal-btn flex items-center justify-center gap-3 py-3 sm:py-4"
                        >
                          <Check size={16} className="sm:w-[18px] sm:h-[18px]" /> Confirmar Ingreso
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'slots' && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
              <div className="glass-card p-6 sm:p-10 border-white/5">
                <h2 className="font-display text-xl sm:text-2xl uppercase tracking-tight mb-6 sm:mb-8 flex items-center gap-3 text-white">
                  <Clock size={20} className="text-brand sm:w-6 sm:h-6" /> Nuevo Bloque
                </h2>
                <form onSubmit={handleAddSlot} className="grid grid-cols-1 md:grid-cols-5 gap-4 sm:gap-6 items-end">
                  <div className="flex flex-col gap-2">
                    <label className="text-[8px] sm:text-[9px] font-bold text-white/30 uppercase tracking-[0.2em] ml-1">Día</label>
                    <select
                      className="luxury-input w-full appearance-none py-3 sm:py-4"
                      value={newSlot.day}
                      onChange={e => setNewSlot({ ...newSlot, day: parseInt(e.target.value) })}
                    >
                      {DAYS.map((d, i) => <option key={i} value={i} className="bg-dark-surface">{d}</option>)}
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[8px] sm:text-[9px] font-bold text-white/30 uppercase tracking-[0.2em] ml-1">Inicio</label>
                    <input type="time" className="luxury-input w-full py-3 sm:py-4" value={newSlot.start} onChange={e => setNewSlot({ ...newSlot, start: e.target.value })} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[8px] sm:text-[9px] font-bold text-white/30 uppercase tracking-[0.2em] ml-1">Fin</label>
                    <input type="time" className="luxury-input w-full py-3 sm:py-4" value={newSlot.end} onChange={e => setNewSlot({ ...newSlot, end: e.target.value })} />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-[8px] sm:text-[9px] font-bold text-white/30 uppercase tracking-[0.2em] ml-1">Cupos</label>
                    <input
                      type="number"
                      min={1}
                      className="luxury-input w-full py-3 sm:py-4"
                      value={newSlot.cap}
                      onChange={e => setNewSlot({ ...newSlot, cap: Math.max(1, parseInt(e.target.value || '1', 10)) })}
                    />
                  </div>

                  <button type="submit" disabled={isAddingSlot} className="brutal-btn w-full flex items-center justify-center gap-2 py-3 sm:py-4">
                    {isAddingSlot ? <Loader2 className="animate-spin" size={16} /> : <><Plus size={16} /> Agregar</>}
                  </button>
                </form>
              </div>

              <div className="glass-card overflow-hidden border-white/5">
                <div className="px-6 sm:px-10 py-4 sm:py-6 border-b border-white/5 bg-white/2 font-display text-[9px] sm:text-[10px] uppercase tracking-[0.2em] flex justify-between items-center text-white/40">
                  <span>Horarios Ofrecidos</span>
                  <span>{slots.length} Bloques</span>
                </div>
                <div className="divide-y divide-white/5">
                  {slots.length === 0 ? (
                    <div className="p-12 sm:p-20 text-center space-y-6">
                      <div className="text-white/20 font-display uppercase tracking-widest">Sin horarios definidos</div>
                      <button
                        onClick={generateDefaultSlots}
                        disabled={loading}
                        className="luxury-btn px-6 py-3 text-[10px] font-bold uppercase tracking-widest border-brand/20 hover:border-brand/50 text-brand"
                      >
                        {loading ? <Loader2 className="animate-spin mx-auto" size={16} /> : 'Generar Horarios Default (11:00 - 16:00)'}
                      </button>
                    </div>
                  ) : (
                    slots.map(slot => (
                      <div key={slot.id} className="p-6 sm:p-8 flex items-center justify-between hover:bg-white/5 transition-all group">
                        <div className="flex items-center gap-4 sm:gap-8">
                          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/5 rounded-xl flex items-center justify-center text-brand font-display text-[10px] sm:text-xs border border-white/5 group-hover:border-brand/30 transition-colors">
                            {DAYS[slot.dayOfWeek].slice(0, 3).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-display text-lg sm:text-2xl uppercase tracking-tight text-white leading-none mb-1 sm:mb-2">{slot.startTime} - {slot.endTime}</p>
                            {editingSlotId === slot.id ? (
                              <div className="flex items-center gap-2 mt-2">
                                <input
                                  type="number"
                                  min={1}
                                  className="luxury-input py-1 px-2 w-20 text-xs"
                                  value={editingCapacity}
                                  onChange={(e) => setEditingCapacity(parseInt(e.target.value) || 1)}
                                />
                                <button
                                  onClick={() => updateSlotCapacity(slot.id, editingCapacity)}
                                  className="text-brand hover:text-white transition-colors p-1"
                                  title="Guardar"
                                >
                                  <Check size={16} />
                                </button>
                                <button
                                  onClick={() => setEditingSlotId(null)}
                                  className="text-white/30 hover:text-white transition-colors p-1"
                                  title="Cancelar"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            ) : (
                              <p className="text-[8px] sm:text-[9px] font-bold text-white/30 uppercase tracking-[0.2em] flex items-center gap-2">
                                Capacidad: {slot.capacity} Atletas
                                <button
                                  onClick={() => {
                                    setEditingSlotId(slot.id);
                                    setEditingCapacity(slot.capacity);
                                  }}
                                  className="opacity-0 group-hover:opacity-100 text-brand hover:underline transition-all ml-2"
                                >
                                  Editar
                                </button>
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {reservations.some(r => r.slotId === slot.id && r.status !== ReservationStatus.CANCELLED) && (
                            <span className="text-[8px] font-bold text-brand uppercase tracking-wider bg-brand/10 px-2 py-1 rounded">Reservado</span>
                          )}
                          <button
                            onClick={() => deleteSlot(slot.id)}
                            className="p-3 sm:p-4 text-white/10 hover:text-red-500 transition-colors"
                            title="Eliminar horario"
                          >
                            <Trash2 size={18} className="sm:w-5 sm:h-5" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'clients' && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
              <div className={`glass-card p-6 sm:p-10 border-white/5 ${!hasGymId ? 'opacity-40 pointer-events-none' : ''}`}>
                <h2 className="font-display text-xl sm:text-2xl uppercase tracking-tight mb-6 sm:mb-8 flex items-center gap-3 text-white">
                  <UserPlus size={20} className="text-brand sm:w-6 sm:h-6" /> Alta de Socio
                </h2>
                <form onSubmit={handleAddClient} className="space-y-6 sm:space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                    <div className="flex flex-col gap-2">
                      <label className="text-[8px] sm:text-[9px] uppercase font-bold text-white/30 tracking-[0.2em]">Nombre Completo</label>
                      <input type="text" placeholder="Atleta Elite" className="luxury-input w-full py-3 sm:py-4" value={newName} onChange={e => setNewName(e.target.value)} required />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[8px] sm:text-[9px] uppercase font-bold text-white/30 tracking-[0.2em]">Email</label>
                      <input type="email" placeholder="atleta@elite.com" className="luxury-input w-full py-3 sm:py-4" value={newEmail} onChange={e => setNewEmail(e.target.value)} required />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[8px] sm:text-[9px] uppercase font-bold text-white/30 tracking-[0.2em]">PIN de Acceso</label>
                      <div className="relative">
                        <input type="text" placeholder="••••••" className="luxury-input w-full pl-12 py-3 sm:py-4 font-mono font-bold text-brand tracking-[0.3em]" value={newPin} onChange={e => setNewPin(e.target.value)} required maxLength={6} />
                        <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={16} />
                        <button type="button" onClick={() => setNewPin(Math.floor(100000 + Math.random() * 900000).toString())} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-brand/10 text-brand rounded hover:bg-brand hover:text-black transition-colors"><Wand2 size={14} /></button>
                      </div>
                    </div>
                  </div>

                  <button type="submit" disabled={isAddingClient} className="brutal-btn w-full sm:w-auto sm:min-w-[200px] flex items-center justify-center gap-3 disabled:opacity-50 py-3 sm:py-4">
                    {isAddingClient ? <Loader2 className="animate-spin" size={16} /> : 'Vincular Socio'}
                  </button>
                  {successMsg && <div className="text-brand font-bold text-[9px] sm:text-[10px] uppercase tracking-widest animate-pulse">Socio registrado correctamente</div>}
                </form>
              </div>

              <div className="glass-card overflow-hidden border-white/5">
                <div className="px-6 sm:px-10 py-4 sm:py-6 border-b border-white/5 bg-white/2 font-display text-[9px] sm:text-[10px] uppercase tracking-[0.2em] flex justify-between items-center text-white/40">
                  <span>Socios Vinculados</span>
                  <span>{authEmails.length} Atletas</span>
                </div>
                <div className="divide-y divide-white/5">
                  {authEmails.length === 0 ? (
                    <div className="p-12 sm:p-20 text-center text-white/20 font-display uppercase tracking-widest">No hay socios autorizados</div>
                  ) : (
                    authEmails.map(client => (
                      <div key={client.email} className="p-6 sm:p-8 flex items-center justify-between hover:bg-white/5 transition-all group">
                        <div className="flex items-center gap-4 sm:gap-8">
                          <div className="w-10 h-10 sm:w-14 sm:h-14 bg-white/5 rounded-full flex items-center justify-center text-brand font-display text-lg sm:text-2xl border border-white/5">{client.full_name.charAt(0)}</div>
                          <div>
                            <p className="font-display text-lg sm:text-xl uppercase tracking-tight text-white leading-none mb-1">{client.full_name}</p>
                            <p className="text-[9px] sm:text-[10px] text-white/30 font-bold uppercase tracking-widest">{client.email}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 sm:gap-6">
                          <div className="flex flex-col items-end">
                            <p className="font-mono font-bold text-brand text-xs sm:text-sm tracking-widest">{showPins[client.email] ? client.pin_code : '••••••'}</p>
                            <button onClick={() => setShowPins(prev => ({ ...prev, [client.email]: !prev[client.email] }))} className="text-[8px] sm:text-[9px] font-bold text-white/20 uppercase tracking-widest hover:text-brand transition-colors">
                              {showPins[client.email] ? 'Ocultar' : 'Ver PIN'}
                            </button>
                          </div>

                          {clientToDelete === client.email ? (
                            <div className="flex items-center gap-2 animate-in slide-in-from-right-4">
                              <button
                                onClick={() => handleDeleteClient(client.email)}
                                disabled={isDeletingClient}
                                className="bg-red-500 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-none text-[8px] sm:text-[9px] font-display uppercase tracking-widest disabled:opacity-50"
                              >
                                {isDeletingClient ? <Loader2 size={10} className="animate-spin" /> : 'OK'}
                              </button>
                              <button
                                onClick={() => setClientToDelete(null)}
                                className="p-1.5 sm:p-2 text-white/20 hover:text-white"
                              >
                                <X size={14} className="sm:w-4 sm:h-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setClientToDelete(client.email)}
                              className="p-3 sm:p-4 text-white/10 hover:text-red-500 transition-colors bg-white/2 rounded-xl"
                            >
                              <Trash2 size={16} className="sm:w-[18px] sm:h-[18px]" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'chat' && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="glass-card overflow-hidden border-white/5 h-[500px] flex flex-col">
                  <div className="p-4 border-b border-white/5 bg-white/2">
                    <h3 className="font-display text-[10px] uppercase tracking-widest text-white/40">Conversaciones</h3>
                  </div>
                  <div className="flex-1 overflow-y-auto divide-y divide-white/5 no-scrollbar">
                    {chatConversations.length === 0 ? (
                      <div className="p-10 text-center text-white/20 text-[10px] uppercase font-bold tracking-widest">No hay conversaciones activas</div>
                    ) : (
                      chatConversations.map(client => (
                        <button
                          key={client.id}
                          onClick={() => setSelectedChatClient({ id: client.id, name: client.name })}
                          className={`w-full p-4 flex items-center gap-4 hover:bg-white/5 transition-all text-left relative ${selectedChatClient?.id === client.id ? 'bg-white/5 border-l-2 border-brand' : ''}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-brand font-display border border-white/5">
                            {client.name.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-display text-sm uppercase tracking-tight text-white leading-none mb-1 truncate">{client.name}</p>
                            <p className="text-[9px] text-white/30 font-bold uppercase tracking-widest">Ver chat</p>
                          </div>
                          {unreadClientIds.has(client.id) && (
                            <div className="w-3 h-3 bg-emerald-500 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.8)]"></div>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>

                <div className="lg:col-span-2">
                  {selectedChatClient ? (
                    <Chat
                      currentUser={user}
                      targetId={selectedChatClient.id}
                      targetName={selectedChatClient.name}
                    />
                  ) : (
                    <div className="glass-card h-[500px] flex flex-col items-center justify-center text-center p-10 border-dashed border-white/5">
                      <MessageSquare size={48} className="text-white/5 mb-6" />
                      <p className="text-white/20 font-display uppercase tracking-widest text-[10px]">Selecciona un socio para chatear</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'allied-gyms' && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
              <div className="glass-card p-8 border-brand/20 bg-brand/5 flex items-start gap-6">
                <Globe className="text-brand shrink-0" size={32} />
                <div>
                  <h4 className="font-display text-xl uppercase tracking-tight text-white mb-2">Red Brotherhood Elite</h4>
                  <p className="text-[10px] text-white/40 font-bold uppercase tracking-widest leading-relaxed">
                    Sedes fuera de tu zona de influencia <b className="text-brand">({zones.find(z => z.id === gym?.zoneId)?.name})</b>.
                    Los atletas de estas sedes pueden visitarte y viceversa.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {alliedGyms.map(g => (
                  <div key={g.id} className="glass-card overflow-hidden border-white/5 group hover:border-brand/30 transition-all">
                    <div className="h-40 overflow-hidden relative">
                      <img src={g.image} alt={g.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/80 to-transparent"></div>
                      <div className="absolute bottom-4 left-4">
                        <span className="bg-brand text-black text-[8px] font-bold px-2 py-1 uppercase tracking-widest">
                          {zones.find(z => z.id === g.zoneId)?.name}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-display text-xl uppercase tracking-tight text-white group-hover:text-brand transition-colors">{g.name}</h3>
                      <div className="flex items-center gap-2 mt-2 opacity-40">
                        <MapPin size={12} />
                        <span className="text-[9px] font-bold uppercase tracking-widest">Sede Verificada</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6 sm:space-y-8">
          <div className="bg-dark-surface rounded-[32px] sm:rounded-[40px] p-6 sm:p-10 text-white border border-white/5 flex flex-col items-center text-center overflow-hidden relative group">
            <div className="absolute inset-0 bg-brand/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-brand/10 rounded-full flex items-center justify-center mb-6 sm:mb-8 text-brand border border-brand/20 shadow-[0_0_30px_rgba(255,255,255,0.1)] relative z-10">
              <QrCode size={40} className="sm:w-12 sm:h-12" />
            </div>
            <h3 className="font-display text-2xl sm:text-3xl uppercase tracking-tight mb-2 relative z-10">Lector QR</h3>
            <p className="text-[9px] sm:text-[10px] text-white/30 mb-8 sm:mb-10 font-bold uppercase tracking-[0.2em] relative z-10">Validación de Atletas</p>

            {showScanner ? (
              <div className="w-full space-y-4 sm:space-y-6 relative z-10">
                <div id="reader" className="w-full rounded-2xl overflow-hidden bg-black aspect-square border-2 border-brand/50"></div>
                <button
                  onClick={() => setShowScanner(false)}
                  className="w-full py-3 sm:py-4 bg-white/5 text-white/60 rounded-xl font-display uppercase tracking-widest text-[9px] sm:text-[10px] hover:bg-white/10 transition-all"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <div className="w-full space-y-4 sm:space-y-6 relative z-10">
                <button
                  onClick={() => setShowScanner(true)}
                  className="brutal-btn w-full flex items-center justify-center gap-3 py-3 sm:py-4"
                >
                  Abrir Cámara
                </button>

                <div className="relative pt-2 sm:pt-4">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-white/5"></div>
                  </div>
                  <div className="relative flex justify-center text-[7px] sm:text-[8px] uppercase font-bold tracking-[0.3em]">
                    <span className="bg-dark-surface px-3 text-white/20">O ingreso manual</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="CÓDIGO..."
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                    className="flex-1 luxury-input px-3 sm:px-4 py-3 sm:py-4 text-xs sm:text-sm font-mono font-bold tracking-widest"
                  />
                  <button
                    onClick={() => handleCheckInByCode(manualCode)}
                    disabled={isProcessingCode || !manualCode}
                    className="bg-brand text-black p-3 sm:p-4 rounded-xl hover:scale-105 active:scale-95 disabled:opacity-50 transition-all"
                  >
                    {isProcessingCode ? <Loader2 className="animate-spin" size={18} /> : <Check size={20} className="sm:w-6 sm:h-6" />}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="glass-card p-6 sm:p-10 border-white/5">
            <h4 className="font-display text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/30 mb-6 sm:mb-8 flex items-center gap-3">
              <MapPin size={14} className="text-brand sm:w-4 sm:h-4" /> Sede Info
            </h4>
            <div className="space-y-4 sm:space-y-6">
              <div className="flex justify-between items-center py-2 sm:py-3 border-b border-white/5">
                <span className="text-[8px] sm:text-[9px] font-bold text-white/20 uppercase tracking-widest">Zona Territorial</span>
                <span className="text-xs sm:text-sm font-display uppercase text-white">
                  {zones.find(z => z.id === gym?.zoneId)?.name || 'No Asignada'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 sm:py-3 border-b border-white/5">
                <span className="text-[8px] sm:text-[9px] font-bold text-white/20 uppercase tracking-widest">Visitas Hoy</span>
                <span className="text-xs sm:text-sm font-display uppercase text-brand">{todayCount}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-dark-bg/60 backdrop-blur-md flex items-center justify-center z-50">
          <div className="glass-card p-10 flex flex-col items-center gap-6 border-brand/20">
            <Loader2 className="animate-spin text-brand" size={40} />
            <span className="font-display text-sm uppercase tracking-[0.3em] text-white">Sincronizando...</span>
          </div>
        </div>
      )}
      <LegalFooter />
    </div>
  );
};

export default GymPortal;
